import React, { useState, useEffect, useRef } from 'react';
import { Box, Button, IconButton, Menu, MenuItem, Divider, Typography, CircularProgress, Tooltip } from '@mui/material';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import MenuBookIcon from '@material-ui/icons/MenuBook';
import CheckIcon from '@material-ui/icons/Check';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import AssignmentIcon from '@material-ui/icons/Assignment';
import BarChartIcon from '@material-ui/icons/BarChart';
import EditIcon from '@material-ui/icons/Edit';
import FullscreenExitIcon from '@material-ui/icons/FullscreenExit';

import { DQ_PRO_PLAN_KEY } from '../../../../../utils/constants';
import ProgramHeaderBanner from './ProgramHeaderBanner.comp';
import ProgramPreviewCard from './ProgramCreationPreview.comp';
import BuildingProgramView from './BuildingProgramView.comp';
import AssessmentTab from './AssessmentTab.comp';
import IndividualDashboardPreview from './IndividualDashboardPreview.comp';
import EditProgramInfoDialog from './Dialogs/EditProgramInfoDialog.comp';
import LearnTab from './LearnTab/LearnTab.comp';
import SlideDeckDoc from './LearnTab/SlideDeckDoc.comp';
import useSlideDeckHtml from '../../hooks/LearnTab/useSlideDeckHtml';
import { tUi } from '../../../../../utils/dq_lc';
import { Stars } from '@material-ui/icons';
import AccountMenu from '../../../../../components/AccountMenu/AccountMenu.comp';

const ProgramPreview = ({
    classes,
    isOutlineReady,
    isContentBuilding,
    programDisplayTitle,
    programMeta,
    overallProgress,
    authUser,
    history,
    teacherDash,
    currentPlanKey,
    setShowPublishDialog,
    setShowSaveProgramDialog,
    setShowPlans,
    // The account control owns its own state now; see AccountMenu.
    isProgramBuilding,
    // Set by the "Preview learning program" button on the build-complete screen.
    userClickedPreview = false,
    contentProgress,
    buildProgressStatus = 0,
    isResumingBuild = false,
    setUserClickedPreview,
    curationStage = '',
    buildStarted = false,
    chatHistory,
    wizard,
    onWizardSave,
    activeTab,
    handleTabChange,
    isTabTransitioning,
    dq_module_code,
    programIdState,
    programData,
    programDetails,
    modules = [],
    isPlanLoading,
    step1Progress = 0,
    isProgramEditable = true,
    canEditProgram = false,
    canPublishProgram = false,
    // Sample Programs gallery preview of a DQ-authored template — forwarded to the
    // embedded DQProProgPreview so it hides its own edit tooling and reads through
    // the templates/* endpoints rather than the owner-scoped ones.
    readOnly = false,
    isPublicPreview = false,
    publishing = false,
    onPublishProgram,
    editDialogOpen,
    setEditDialogOpen,
    onEditSaved,
    onTopUpCredits,     // (shortfall info) → the payment window for a rebuild refused for credits
    isChatOpen = false,
}) => {
    const TAB_DESCRIPTIONS = {
        1: <>The following is your program learning content. <strong>Click 'View' to see.</strong> Click <strong>"View Program Outline"</strong> if you would like to see the overall curriculum.</>,
        2: <>See a sample assessment below that learners will take. They will take a pre and post version so that you and your clients can visualize impact in real-time. Try the assessment below.</>,
        3: <><strong>Each learner's dashboard — measurable impact, ready to share</strong><br /><br />Every learner who takes your program gets this personal view of their online safety readiness, support at home and school, and learning progress — with class averages to compare against and clear next steps. Here's a sample, populated with example data; real numbers build as your learners complete assessments and programs.</>,
    };

    const renderTabContent = () => {
        return (
            <>
                {/* Learn tab — always mounted once programData is available so navigating
                    back to it is instant (no re-fetch). Hidden via CSS when not active. */}
                {programData && (
                    <Box sx={{
                        flex: 1, minHeight: 0,
                        display: activeTab === 1 ? 'flex' : 'none',
                        flexDirection: 'column',
                        overflow: 'hidden',
                    }}>
                        <LearnTab
                            key={learnTabKey}
                            embedded={true}
                            readOnly={readOnly}
                            isPublicPreview={isPublicPreview}
                            programCode={dq_module_code || programIdState}
                            focusMode={inFocusMode}
                            onToggleFocusMode={toggleFocusMode}
                            onSignUp={() => setShowSaveProgramDialog(true)}
                            onUpgrade={() => setShowPlans(true)}
                        />
                    </Box>
                )}

                {/* Assess tab — lightweight, mount/unmount is fine */}
                {activeTab === 2 && programData && (
                    <Box sx={{ maxWidth: '1550px', width: '100%', mx: 'auto', px: { xs: 2, md: 3 } }}>
                        <AssessmentTab programData={programData} modules={modules} classes={classes} />
                    </Box>
                )}

                {/* View Impact tab */}
                {activeTab === 3 && (
                    <Box sx={{ maxWidth: '1550px', width: '100%', mx: 'auto', px: { xs: 2, md: 3 } }}>
                        <IndividualDashboardPreview programName={programData?.full_name} />
                    </Box>
                )}
            </>
        );
    };
    const [learnTabKey, setLearnTabKey] = useState(0);

    const handleTabChangeWithReset = (id) => {
        if (id === 1 && activeTab === 1) {
            setLearnTabKey(k => k + 1);
        }
        handleTabChange(id);
    };

    // ── Unified building phase state ─────────────────────────────────────────
    // showPreviewCard: true for first 2.5s, then fades out (fadeOut flips first)
    const isBuilding = !isPublicPreview && (isProgramBuilding || isContentBuilding);
    const [showPreviewCard, setShowPreviewCard] = useState(false);
    const [fadeOutPreview, setFadeOutPreview] = useState(false);
    const buildingStartedRef = useRef(false);
    const [isHeaderCollapsed, setIsHeaderCollapsed] = useState(false);
    const scrollContainerRef = useRef(null);
    // Public preview: download every session's slides as one HTML file.
    const deckExport = useSlideDeckHtml({
        courseCode: dq_module_code || programIdState,
        modules,
        programTitle: programDisplayTitle,
    });

    // Focus mode: hides the header banner + tab bar so the Learn tab's slide
    // preview gets the full height. Toggled from the preview's tools pill,
    // remembered for the session, and exited with Esc or the slim top bar.
    const [focusMode, setFocusMode] = useState(() => {
        try { return sessionStorage.getItem('dq_pro_preview_focus') === '1'; } catch (e) { return false; }
    });
    const toggleFocusMode = () => setFocusMode(prev => {
        const next = !prev;
        try { sessionStorage.setItem('dq_pro_preview_focus', next ? '1' : '0'); } catch (e) { /* ignore */ }
        return next;
    });
    const inFocusMode = focusMode && activeTab === 1 && isOutlineReady && !isBuilding && !showPreviewCard;
    useEffect(() => {
        if (!inFocusMode) return;
        const onKey = (e) => { if (e.key === 'Escape') toggleFocusMode(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [inFocusMode]);

    useEffect(() => {
        if (isBuilding && !buildingStartedRef.current) {
            buildingStartedRef.current = true;
            
            // Only show the transitional preview card if we are starting fresh.
            // (isProgramBuilding is true when we just initiated from chat)
            if (isProgramBuilding && !isContentBuilding) {
                setShowPreviewCard(true);
                setFadeOutPreview(false);
                // After 2.5s begin fade-out (CSS transition 600ms)
                const fadeTimer = setTimeout(() => setFadeOutPreview(true), 2500);
                // After fade completes, hide the card entirely
                const hideTimer = setTimeout(() => setShowPreviewCard(false), 3100);
                return () => { clearTimeout(fadeTimer); clearTimeout(hideTimer); };
            } else {
                setShowPreviewCard(false);
            }
        }
        if (!isBuilding) {
            buildingStartedRef.current = false;
            setShowPreviewCard(false);
            setFadeOutPreview(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isBuilding]);

    // 5 cards = 500 units (each card = 100 units).
    // Outline phase  (progress_status 1-7): fills units 0–250 (cards 0, 1, and first half of card 2).
    // Content phase  (contentProgress 0-100): fills units 250–500 (second half of card 2, cards 3–4).
    // This keeps outline and preview contribution equal (250 units each).
    const totalUnits = isProgramBuilding && !isContentBuilding
        ? Math.round(((buildProgressStatus || 0) / 7) * 250)
        : isContentBuilding
            ? Math.min(500, 250 + Math.round((contentProgress / 100) * 250))
            : 0;

    const CARD_COUNT = 5;
    const activeCardIndex = Math.min(CARD_COUNT - 1, Math.floor(totalUnits / 100));
    const activeCardProgress = Math.round(totalUnits % 100);
    const isBuildComplete = totalUnits >= 500;

    // Header banner: normalise totalUnits (0-500) back to 0-100%
    const unifiedProgress = Math.round(totalUnits / 5);

    // Dynamically extract frameworks to show in the "Framework Alignment" card
    /*
     * code -> readable name, harvested from the assistant's own framework
     * marker.
     *
     * The wizard keeps framework_ids ("SG_MOE_CW101_2018") but the marker carries the
     * objects the assistant described in chat, which have the name too. Read
     * every marker in the history, not just the last: a person can revisit
     * the framework step, and an earlier turn may be the one that named the
     * framework they ended up with.
     */
    const frameworkNameByCode = (() => {
        const index = {};
        (chatHistory || []).forEach((m) => {
            const text = m?.text || '';
            const re = /\{\{detected_?frameworks=([^}]*)\}\}/g;
            let match;
            while ((match = re.exec(text)) !== null) {
                try {
                    const parsed = JSON.parse(decodeURIComponent(match[1]));
                    (parsed || []).forEach((f) => {
                        if (!f || typeof f !== 'object') return;
                        const code = String(f.framework_id || f.code || f.value || '').trim();
                        const name = String(f.FrameName || f.label || f.name || '').trim();
                        // Only when they actually differ — an entry whose "name"
                        // IS the code teaches us nothing and must not shadow a
                        // better one from another turn.
                        if (code && name && code !== name) index[code] = name;
                    });
                } catch (_) { /* a malformed marker is not worth failing over */ }
            }
        });
        return index;
    })();


    const detectedFrameworks = (() => {
        let rawList = [];
        // Use wizard.DetectedFrameworks if available
        if (Array.isArray(wizard?.DetectedFrameworks) && wizard.DetectedFrameworks.length > 0) {
            rawList = wizard.DetectedFrameworks.map(f => f.FrameName || f.label || f);
        }
        // Fallback: extract from wizard.SelectedFrameList
        else if (Array.isArray(wizard?.SelectedFrameList) && wizard.SelectedFrameList.length > 0) {
            rawList = wizard.SelectedFrameList.map(f => (typeof f === 'string' ? f : f?.label || f?.FrameName || f?.value));
        }
        // Final fallback: parse directly from chat history markers
        else {
            const fwMsg = [...chatHistory].reverse().find(m => m.text?.includes('{{detected_frameworks='));
            if (fwMsg) {
                const match = fwMsg.text.match(/\{\{detected_?frameworks=([^}]*)\}\}/);
                if (match) {
                    try {
                        const parsed = JSON.parse(decodeURIComponent(match[1]));
                        rawList = (parsed || []).map(f => f.FrameName || f.label || f);
                    } catch (_) {}
                }
            }
        }

        const result = rawList
            .filter(Boolean)
            .map(f => {
                const cleaned = String(f).replace(/\{\{detected_?frameworks=[^}]*\}\}/g, '').trim();
                /*
                 * The catalogue first — it is authoritative and covers every
                 * framework. The marker map is the fallback for a build whose
                 * program does not exist yet, when there is nothing to look up.
                 * Neither knows it → the code, exactly as before.
                 */
                return frameworkNameByCode[cleaned] || cleaned;
            });

        console.log('[FrameworkDetection] rawList:', rawList);
        console.log('[FrameworkDetection] processed:', result);
        return result;
    })();

    const buildingCards = [
        { title: 'Learning Goals',              items: ['Core Learning Objectives', "Bloom's Taxonomy Levels 2-4"] },
        {
            title: 'Framework Alignment',
            /*
             * Slide 7. Only the frameworks this program actually aligns to.
             *
             * The old fallback printed three fixed names when none were
             * detected yet, which on a card headed "Framework Alignment"
             * reads as a claim about the person's program. Now it says what
             * is true — that alignment is still being worked out — and when
             * there are more than three it says so instead of quietly
             * dropping the rest.
             */
            items: (() => {
                const clean = detectedFrameworks
                    .map(f => String(f).replace(/\{\{detected_?frameworks=[^}]*\}\}/g, '').trim())
                    .filter(Boolean);
                if (!clean.length) return ['No external framework', 'Aligning to DQ standards only'];
                const shown = clean.slice(0, 3);
                const rest = clean.length - shown.length;
                return rest > 0 ? [...shown, `+${rest} more`] : shown;
            })(),
        },
        { title: 'Lesson Content + Activities', items: ['Intro Video', 'Slides', 'Group Activity', 'Reflection'] },
        { title: 'Pre + Post Assessment',       items: ['Questions', 'Scoring Preview*', 'Impact Data'] },
        { title: 'Impact Dashboard',            items: ['Before/After Scores', 'Class Benchmarks', 'Reports'] },
    ];

    const renderMainContent = () => {
        // ── Building phase: preview card (timed) → BuildingProgramView ─────────
        // We use buildingStartedRef.current to catch the very first render where isBuilding is true
        // but the useEffect hasn't run yet to set showPreviewCard.
        if (isBuilding || showPreviewCard || (isProgramBuilding && !buildingStartedRef.current)) {
            return (
                <Box sx={{ position: 'relative', flex: 1, width: '100%', height: '100%' }}>
                    {/* Transitional preview card — fades out after 2.5s */}
                    {(showPreviewCard || (isBuilding && !buildingStartedRef.current)) && (
                        <Box
                            sx={{
                                position: 'absolute', inset: 0,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                padding: '40px',
                                zIndex: 2,
                                opacity: fadeOutPreview ? 0 : 1,
                                transition: 'opacity 0.6s ease',
                                pointerEvents: fadeOutPreview ? 'none' : 'auto',
                            }}
                        >
                            <ProgramPreviewCard
                                background="var(--brand-surface, #FFFFFF)"
                                preTitle="While you wait..."
                                stat="88%"
                                statColor="#FF6900"
                                title="of Grade 7 students globally can't reliably identify AI-generated content."
                                subtitle="Your program directly addresses this — and you'll have the data to prove it worked."
                            />
                        </Box>
                    )}
                    {/* BuildingProgramView — fades in as preview card fades out */}
                    <Box
                        sx={{
                            position: 'absolute', inset: 0,
                            opacity: (fadeOutPreview || (!showPreviewCard && buildingStartedRef.current)) ? 1 : 0,
                            transition: 'opacity 0.6s ease',
                            overflow: 'auto',
                        }}
                    >
                        <BuildingProgramView
                            activeCardIndex={activeCardIndex}
                            activeCardProgress={activeCardProgress}
                            cards={buildingCards}
                            isComplete={isBuildComplete}
                            onPreviewClick={() => setUserClickedPreview(true)}
                        />
                    </Box>
                </Box>
            );
        }

        // Show a centered spinner while the initial status check is in flight
        // (only for existing in-progress programs restored on page load).
        if (isResumingBuild) {
            return (
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                    <CircularProgress size={40} sx={{ color: '#FF6900' }} />
                    <Typography sx={{ fontSize: 14, color: '#888' }}>Loading your program…</Typography>
                </Box>
            );
        }

        if (!isOutlineReady) {
            if (isPublicPreview) return <></>
            /*
             * The left panel is retired — confirmed with Dr. Park in the 10 Sep
             * team meeting. While the program is being customised the chat IS
             * the screen; an info card beside it repeated what the assistant
             * was already saying.
             *
             * The time-options card below still renders: it is a control the
             * conversation hands off to, not descriptive copy.
             */
            const hasTimeOptions = chatHistory.some(msg => msg.subType === 'time_options');
            if (!hasTimeOptions) return <></>;
            
            return (
                /*
                 * Slides 5-6: the card sits to the LEFT while the chat holds the
                 * centre. Centring it here put it under the middle of the whole
                 * viewport, which — with the chat docked over the right half —
                 * left it looking like the page was about the card.
                 *
                 * flex-start on a wide screen only. Below the chat's breakpoint
                 * the chat goes full-screen and there is no second column to sit
                 * beside, so the card centres as before.
                 */
                <Box sx={{
                    flex: 1, display: 'flex', padding: '40px',
                    justifyContent: { xs: 'center', md: 'flex-start' },
                    /*
                     * Centred by auto margins rather than align-items, which
                     * clips both ends when a card is ever taller than the
                     * column. overflowY is the escape hatch for a long chat
                     * card; the slide-6 card itself is short enough not to
                     * need it.
                     */
                    overflowY: 'auto',
                    '& > *': { marginTop: 'auto', marginBottom: 'auto' },
                }}>
                    {hasTimeOptions ? (
                        <ProgramPreviewCard
                            icon={<MenuBookIcon style={{ color: '#FF6900', fontSize: 30 }} />}
                            iconBg="#FDEEE9"
                            iconRound={true}
                            title={`Great — ${wizard?.Theme || wizard?.Title || 'your program is taking shape!'}`}
                            subtitle="Tell us your time constraints and we'll build a complete, assessment-backed program instantly."
                            items={[
                                {
                                    icon: <InfoOutlinedIcon style={{ color: '#FF6900', fontSize: 20, marginTop: 2, flexShrink: 0 }} />,
                                    bg: 'var(--brand-surface, #F7F7F8)', textColor: 'var(--brand-text, #333)',
                                    bold: 'Can be embedded', text: ' in any subject — maths, science, social studies.',
                                },
                                {
                                    icon: <CheckIcon style={{ color: '#FF6900', fontSize: 20, marginTop: 2, flexShrink: 0 }} />,
                                    bg: 'var(--brand-surface, #F7F7F8)', textColor: 'var(--brand-text, #333)',
                                    bold: 'Your national standards,', text: ' automatically mapped.',
                                },
                            ]}
                        />
                    ) : (
                        <ProgramPreviewCard
                            icon={<AccessTimeIcon style={{ color: '#EE6628', fontSize: 30 }} />}
                            iconBg="#FDEEE9"
                            iconRound={false}
                            /*
                             * Slide 6, verbatim.
                             *
                             * The four items are the levers the deck names under
                             * "including:" — what the person can still steer after the
                             * first draft exists. They are not interchangeable with
                             * general reassurances about quality: the slide is answering
                             * "what will I still control?", not "will it be any good?".
                             * Order and wording follow the slide.
                             */
                            title="Your program will appear soon."
                            paragraphs={[
                                "Answer a couple of questions on the right, and we'll build a complete, assessment-backed first draft in minutes.",
                                "The first draft is just a starting point. Once it's ready, tell me anytime and we'll adjust it together — either through this chat, or the Edit Option, including:",
                            ]}
                            bullets={[
                                { bold: 'National standards', text: ' — choose which framework to align to' },
                                { bold: 'Subjects to embed', text: ' — maths, science, social studies, and more' },
                                { bold: 'Cultural sensitivities', text: ' — tailor tone, examples, and local context' },
                                { bold: 'Learning outcome focus', text: ' — emphasise the skills that matter most to you' },
                            ]}
                            footnote="Nothing is locked in — set your time to begin, then refine as much as you like."
                        />
                    )}
                </Box>
            );
        }

    };

    return (
        <Box className={classes.contentArea}>
            {/* Off-screen (not display:none, so slide layouts can measure themselves) while exporting. */}
            {deckExport.deck && (
                <div aria-hidden="true" style={{ position: 'fixed', left: -20000, top: 0 }}>
                    <SlideDeckDoc
                        ref={deckExport.deckRef}
                        programTitle={programDisplayTitle}
                        courseCode={dq_module_code || programIdState}
                        sessions={deckExport.deck}
                    />
                </div>
            )}
            {!inFocusMode && <ProgramHeaderBanner
                    overallProgress={isBuilding ? unifiedProgress : isOutlineReady ? 100 : 0}
                    timelineActiveIndex={
                        /*
                         * The stage the person is actually at.
                         *
                         * This used to fall back to 1, so "Framework" was lit from the
                         * very first message and never moved — Customization was never
                         * shown as current, and reaching the framework step changed
                         * nothing on screen. Now it starts at Customization and
                         * advances when the assistant raises frameworks, which it marks
                         * with a detected_frameworks marker.
                         *
                         * The building and outline-ready branches are untouched.
                         */
                        /*
                         * 3, so Building TICKS, from the moment the build is
                         * COMMITTED — not merely while it is running.
                         *
                         * The banner ticks a step with `i < activeStep`, so an
                         * index of 2 leaves Building an empty ring. Keying that on
                         * isBuilding alone was not enough: a build that fails on
                         * its first outline poll clears isProgramBuilding within
                         * seconds, the index falls back to the summary branch, and
                         * Building un-ticks itself while the failure notice is
                         * still on screen.
                         *
                         * buildStarted is the durable fact — the server took the
                         * request and the credits are spent — so the step stays
                         * ticked through the build, through a failure and its
                         * retry, and across a reload.
                         */
                        /*
                         * 4 — every step ticked, Preview included, from the moment the
                         * person presses "Preview learning program" on the build-complete
                         * screen (BuildingProgramView -> setUserClickedPreview).
                         *
                         * Keyed on that click, and placed FIRST: buildStarted stays true
                         * after a build finishes, so the branch below pinned the index at
                         * 3 and the last step could never tick. A program opened straight
                         * from the list has the click implied, so it ticks on arrival.
                         */
                        (userClickedPreview && isOutlineReady && !isProgramBuilding) ? 4 :
                        (isBuilding || buildStarted) ? 3 :
                        isOutlineReady ? 4 :
                        chatHistory.some(m => m.subType === 'time_options') ? 2 :
                        /*
                         * The Framework step begins the moment the assistant starts
                         * TALKING about frameworks — the intro ("your program can be
                         * built on an existing framework or standard"), the recommended
                         * table, or the question that follows.
                         *
                         * Narrower patterns were tried and each lit a turn late: the
                         * detected_frameworks marker is only emitted once a framework is
                         * SELECTED, and "Recommended frameworks" misses the intro turn
                         * and the phrasings that say "another framework" instead.
                         *
                         * The word itself is the signal, and it is specific enough: the
                         * competency step says "competencies" throughout and never
                         * mentions a framework, so it stays grey.
                         */
                        /*
                         * The stage the SERVER says we are on.
                         *
                         * collected_view lists the curation fields in the order the AI
                         * collects them, each with a value that is null until answered.
                         * selected_frameworks is the boundary: the fields before it are
                         * Customization, and reaching it IS the Framework step.
                         *
                         * Read from the server rather than the reply text because every
                         * text signal was either late or wrong — the detected_frameworks
                         * marker is emitted on SELECTION, and the wording of the
                         * framework turn varies. SelectedFrameList is kept as a fallback
                         * for a restored session that has no collected_view yet.
                         */
                        (() => {
                            /*
                             * A step is CURRENT while it is being asked, and TICKS once
                             * it has been answered — the banner draws i < index as done
                             * and i === index as current, so advancing the index is what
                             * ticks the step behind it.
                             *
                             * alignment is the framework question, so it holds index 1
                             * (Customization ticked, Framework current). Every stage
                             * after it means the framework was ANSWERED, so the index
                             * moves to 2 and Framework ticks.
                             */
                            const AFTER_ALIGNMENT = [
                                'curriculum', 'subject', 'topic', 'sensitivity',
                                'style', 'languages', 'summary',
                            ];
                            if (AFTER_ALIGNMENT.includes(curationStage)
                                || wizard?.SelectedFrameList?.length > 0) return 2;
                            if (curationStage === 'alignment') return 1;
                            return 0;
                        })()
                    }
                    isCollapsed={isHeaderCollapsed}
                    onToggleCollapse={() => setIsHeaderCollapsed(prev => !prev)}
                    leftActions={isPublicPreview ? (
                        <Typography sx={{ fontSize: 16, fontWeight: 700, color: 'var(--brand-text, #1a1a1a)', ml: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {programDisplayTitle}
                        </Typography>
                    ) : null}
                    /* Edit Program Information moved into the chat header
                       (ChatSidebar) — see canEditProgram/onEditProgram there. */
                    actions={!isPublicPreview ? (
                        <>
                            {authUser && !authUser.is_guest && (
                                <Button
                                    className={classes.newChatBtn}
                                    variant="contained"
                                    size="small"
                                    onClick={() => history.push({ pathname: '/my_workspace' })}
                                >
                                    My Workspace
                                </Button>
                            )}
                            {!isPlanLoading && (() => {
                                const isPaidUser = authUser?.dq_pro_plan?.status === 'active'
                                    && authUser?.dq_pro_plan?.plan_key !== DQ_PRO_PLAN_KEY.DEFAULT;
                                const hasPaidPlan = isPaidUser
                                    || (currentPlanKey && currentPlanKey !== DQ_PRO_PLAN_KEY.DEFAULT);
                                return !hasPaidPlan && (teacherDash || !currentPlanKey || currentPlanKey === DQ_PRO_PLAN_KEY.DEFAULT);
                            })() && (
                                <Button
                                    variant="contained"
                                    className={classes.upgradeBtn}
                                    onClick={() => {
                                        if (teacherDash) setShowPublishDialog(true);
                                        else if (!authUser || authUser.is_guest) setShowSaveProgramDialog(true);
                                        else setShowPlans(true);
                                    }}
                                    size="small"
                                    startIcon={<Stars style={{ color: '#ffff', fontSize: 18 }} />}
                                >
                                    {teacherDash ? 'Publish Program' : 'Upgrade'}
                                </Button>
                            )}
                            {/* Same treatment as the step-0 page: orange ring and
                                glyph, not the grey default, so the account control
                                looks like one control across the flow. */}
                            <AccountMenu />
                        </>
                    ) : (
                        <Button
                            variant="contained"
                            size="small"
                            onClick={deckExport.startExport}
                            disabled={deckExport.exporting || !modules?.length}
                            startIcon={deckExport.exporting ? <CircularProgress size={14} color="inherit" /> : null}
                            data-testid="slide-deck-download-button"
                        >
                            {tUi(deckExport.exporting ? 'preview.deck.preparing' : 'preview.deck.download')}
                        </Button>
                    )}
                />}
            {/* Focus mode: slim bar replacing banner + tabs, so users always
                know where they are and have a visible way out. */}
            {inFocusMode && (
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    px: 2,
                    py: 0.5,
                    background: 'var(--brand-surface, #fff)',
                    borderBottom: '1px solid #eee',
                    flexShrink: 0,
                }}>
                    <Typography sx={{
                        fontSize: 13,
                        color: '#6b7280',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}>
                        {programDisplayTitle} · Learn
                    </Typography>
                    <Button
                        size="small"
                        onClick={toggleFocusMode}
                        startIcon={<FullscreenExitIcon style={{ fontSize: 16 }} />}
                        sx={{
                            textTransform: 'none',
                            color: '#0097b2',
                            border: '1px solid #0097b2',
                            borderRadius: '999px',
                            fontSize: 13,
                            lineHeight: 1.4,
                            padding: '2px 12px',
                            flexShrink: 0,
                        }}
                    >
                        Exit focus (Esc)
                    </Button>
                </Box>
            )}
            <Box
                className={classes.innerContent}
                ref={scrollContainerRef}
                sx={{ flex: 1, overflowY: activeTab === 1 ? 'hidden' : 'auto', backgroundColor: 'var(--brand-bg, #FAF8F4)' }}
            >
                {/* Tabs: shown on preview screen (outline ready, not building, not focus mode) */}
                {isOutlineReady && !isBuilding && !showPreviewCard && !inFocusMode && (
                    <Box sx={{
                        background: 'var(--brand-surface, #fff)',
                        px: { xs: 1.5, md: 4 }, pt: { xs: 1.5, md: 2.5 }, pb: { xs: 1, md: 1.5 },
                        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1,
                        position: 'relative',
                    }}>
                        {/* Edit Program Information + Publish — persistent across all tabs,
                            not just while the chat panel happens to be open on the Learn tab. */}
                        {(canEditProgram || canPublishProgram) && (
                            <Box sx={{
                                position: 'absolute',
                                right: { xs: 12, md: 32 },
                                top: { xs: 12, md: 20 },
                                display: 'flex',
                                alignItems: 'center',
                                gap: { xs: 1, md: 1.5 },
                            }}>
                                {canEditProgram && (
                                    <Button
                                        onClick={() => setEditDialogOpen(true)}
                                        startIcon={<EditIcon sx={{ fontSize: 16 }} />}
                                        title="Edit Program Information"
                                        sx={{
                                            background: '#EE6628',
                                            color: '#fff !important',
                                            fontWeight: 500,
                                            fontSize: { xs: 0, md: 14 },
                                            textTransform: 'none',
                                            borderRadius: '999px',
                                            padding: { xs: '8px', md: '6px 16px' },
                                            minWidth: { xs: '32px', md: 'auto' },
                                            boxShadow: 'none',
                                            '& .MuiButton-startIcon': { mr: { xs: 0, md: 1 } },
                                            '&:hover': { background: '#d95a1f' },
                                        }}
                                    >
                                        <Box component="span" sx={{ display: { xs: 'none', md: 'inline' } }}>
                                            Edit Program
                                        </Box>
                                    </Button>
                                )}
                                {canPublishProgram && (
                                    <Button
                                        onClick={onPublishProgram}
                                        disabled={publishing}
                                        title="Publish this program to make it available to schools"
                                        sx={{
                                            background: publishing ? '#BDBDBD' : '#FF6900',
                                            color: '#fff !important',
                                            fontWeight: 700,
                                            fontSize: { xs: 12, md: 14 },
                                            textTransform: 'none',
                                            borderRadius: '999px',
                                            padding: { xs: '6px 10px', md: '6px 16px' },
                                            boxShadow: 'none',
                                            '&:hover': { background: publishing ? '#BDBDBD' : '#c9541e' },
                                            '&.Mui-disabled': { background: '#BDBDBD', color: '#fff !important' },
                                        }}
                                    >
                                        {publishing ? 'Publishing...' : 'Publish'}
                                    </Button>
                                )}
                            </Box>
                        )}
                        <Box sx={{ display: 'flex', gap: { xs: 1, md: 2 }, justifyContent: 'center', width: '100%', alignItems: 'center' }}>
                            {[
                                { id: 1, label: 'Learn',       Icon: MenuBookIcon },
                                { id: 2, label: 'Assess',      Icon: AssignmentIcon },
                                ...(!isPublicPreview ? [{ id: 3, label: 'View Impact', Icon: BarChartIcon }] : [])
                            ].map(({ id, label, Icon }) => {
                                const active = activeTab === id;
                                const iconColor = active ? 'rgba(243,244,246,1)' : 'rgba(74,85,101,1)';
                                return (
                                    <Button
                                        key={id}
                                        onClick={() => handleTabChangeWithReset(id)}
                                        disableRipple
                                        title={label}
                                        sx={{
                                            minWidth: { xs: 0, sm: 130, md: 170 },
                                            flex: { xs: 1, md: '0 0 auto' },
                                            background: active ? 'rgba(106,142,201,1)' : 'rgba(243,244,246,1)',
                                            color: `${iconColor} !important`,
                                            fontWeight: 500,
                                            fontSize: { xs: 13, md: 16 },
                                            textTransform: 'none',
                                            borderRadius: '999px',
                                            padding: { xs: '10px 6px', sm: '6px 10px', md: '8px 24px' },
                                            boxShadow: 'none',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: { xs: 0, sm: '4px', md: '6px' },
                                            '&:hover': { background: active ? 'rgba(86,122,181,1)' : 'rgba(229,231,235,1)' },
                                        }}
                                    >
                                        <Icon sx={{ fontSize: { xs: 22, sm: 16 }, color: iconColor }} />
                                        <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                                            {label}
                                        </Box>
                                    </Button>
                                );
                            })}
                            {/* Tab description moved into a tooltip to reclaim the
                                vertical space the old paragraph took on every visit. */}
                            {TAB_DESCRIPTIONS[activeTab] && (
                                <Tooltip title={TAB_DESCRIPTIONS[activeTab]} arrow placement="bottom-end">
                                    <IconButton size="small" aria-label="About this tab" sx={{ color: '#9ca3af', ml: 0.5 }}>
                                        <InfoOutlinedIcon sx={{ fontSize: 20 }} />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Box>
                    </Box>
                )}

                {isOutlineReady && !isBuilding && !showPreviewCard ? renderTabContent() : renderMainContent()}
            </Box>


            <EditProgramInfoDialog
                open={editDialogOpen}
                onClose={() => setEditDialogOpen(false)}
                wizard={wizard}
                programCode={wizard.CourseCode || programIdState || dq_module_code}
                onSave={onWizardSave}
                onSaved={onEditSaved}
                chatHistory={chatHistory}
                programDetails={programDetails}
                onTopUpCredits={onTopUpCredits}
                isChatOpen={isChatOpen}
            />

        </Box>
    );
};

export const ContentArea = ProgramPreview;
export { ProgramPreview };
export default ProgramPreview;
